/**
 * Physics Manager - Manages physics simulation using Havok Physics V2
 * 
 * Features:
 * - Havok physics engine integration
 * - Physics body creation and management
 * - Collision detection and events
 * - Raycasting
 * - Debug visualization
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/physics
 * Havok Plugin: https://doc.babylonjs.com/features/featuresDeepDive/physics/havokPlugin
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { HavokPlugin } from "@babylonjs/core/Physics/v2/Plugins/havokPlugin";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsBody } from "@babylonjs/core/Physics/v2/physicsBody";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { PhysicsViewer } from "@babylonjs/core/Debug/physicsViewer";
// Debug visualization imports
import "@babylonjs/core/Physics/v2/physicsEngineComponent";
import "@babylonjs/core/Debug/debugLayer";

import { IDisposable, IUpdatable } from "../core/Engine.types";
import { 
    PhysicsConfig, 
    PhysicsBodyOptions, 
    RaycastResult,
    PhysicsTimeConfig,
    PhysicsTimeState,
    TimeScalePreset
} from "./Physics.types";

// Import Havok - this is the WASM physics engine
import HavokPhysics from "@babylonjs/havok";

/**
 * Default time configuration
 */
const DEFAULT_TIME_CONFIG: PhysicsTimeConfig = {
    timeScale: 1,
    targetTimeScale: 1,
    accelerationRate: 2,
    decelerationRate: 4,
    minTimeScale: 0,
    maxTimeScale: 10,
};

/**
 * PhysicsManager - Wraps Babylon.js Physics V2 with Havok
 * 
 * Now includes time acceleration/deceleration support for:
 * - Slow motion effects
 * - Fast forward
 * - Pause/resume
 * - Smooth time scale transitions
 */
export class PhysicsManager implements IDisposable, IUpdatable {
    private _scene: Scene;
    private _config: PhysicsConfig;
    private _havokPlugin: HavokPlugin | null = null;
    private _havokInstance: any = null;
    private _isInitialized: boolean = false;
    private _aggregates: Map<string, PhysicsAggregate> = new Map();
    private _debugEnabled: boolean = false;
    private _physicsViewer: PhysicsViewer | null = null;
    
    // Time scaling properties
    private _timeConfig: PhysicsTimeConfig;
    private _isPaused: boolean = false;
    private _scaledDeltaTime: number = 0;
    private _onTimeScaleChanged?: (state: PhysicsTimeState) => void;

    /**
     * Check if physics is initialized and ready
     */
    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Get the Havok plugin instance
     */
    public get plugin(): HavokPlugin | null {
        return this._havokPlugin;
    }

    /**
     * Get the current gravity
     */
    public get gravity(): Vector3 {
        return this._config.gravity.clone();
    }
    
    /**
     * Get current time scale
     */
    public get timeScale(): number {
        return this._timeConfig.timeScale;
    }
    
    /**
     * Get target time scale
     */
    public get targetTimeScale(): number {
        return this._timeConfig.targetTimeScale;
    }
    
    /**
     * Check if physics time is paused
     */
    public get isPaused(): boolean {
        return this._isPaused;
    }
    
    /**
     * Get the scaled delta time (for physics calculations)
     */
    public get scaledDeltaTime(): number {
        return this._scaledDeltaTime;
    }
    
    /**
     * Check if time is currently accelerating
     */
    public get isAccelerating(): boolean {
        return this._timeConfig.timeScale < this._timeConfig.targetTimeScale;
    }
    
    /**
     * Check if time is currently decelerating
     */
    public get isDecelerating(): boolean {
        return this._timeConfig.timeScale > this._timeConfig.targetTimeScale;
    }

    constructor(scene: Scene, config?: Partial<PhysicsConfig>) {
        this._scene = scene;
        this._config = {
            gravity: config?.gravity ?? new Vector3(0, -9.81, 0),
            debugEnabled: config?.debugEnabled ?? false,
            timestep: config?.timestep ?? 1 / 60,
            maxSubSteps: config?.maxSubSteps ?? 10,
        };
        
        // Initialize time configuration
        this._timeConfig = { ...DEFAULT_TIME_CONFIG };
    }

    /**
     * Initialize the physics engine (async - must be called before using physics)
     */
    public async initialize(): Promise<void> {
        if (this._isInitialized) {
            console.warn("PhysicsManager: Already initialized");
            return;
        }

        try {
            // Initialize Havok WASM module with explicit path to WASM file
            this._havokInstance = await HavokPhysics({
                locateFile: (file: string) => {
                    // In dev mode, Vite serves from public folder at root
                    // In production, the WASM is in the assets folder
                    if (file.endsWith('.wasm')) {
                        return `/HavokPhysics.wasm`;
                    }
                    return file;
                }
            });
            
            // Create Havok plugin
            this._havokPlugin = new HavokPlugin(true, this._havokInstance);
            
            // Enable physics on the scene
            this._scene.enablePhysics(this._config.gravity, this._havokPlugin);
            
            this._isInitialized = true;
            
            // Enable debug if configured
            if (this._config.debugEnabled) {
                this.setDebugEnabled(true);
            }

            console.log("PhysicsManager: Havok Physics V2 initialized");
        } catch (error) {
            console.error("PhysicsManager: Failed to initialize Havok", error);
            throw error;
        }
    }

    /**
     * Add physics to a mesh using PhysicsAggregate (recommended approach)
     * 
     * @param mesh - The mesh to add physics to
     * @param shapeType - The collision shape type
     * @param options - Physics body options
     * @param id - Optional unique identifier for this aggregate
     * @returns The created PhysicsAggregate
     */
    public addPhysicsAggregate(
        mesh: Mesh | AbstractMesh,
        shapeType: PhysicsShapeType,
        options: PhysicsBodyOptions,
        id?: string
    ): PhysicsAggregate {
        this._ensureInitialized();

        const aggregate = new PhysicsAggregate(
            mesh as TransformNode,
            shapeType,
            {
                mass: options.mass,
                restitution: options.restitution ?? 0.2,
                friction: options.friction ?? 0.5,
            },
            this._scene
        );

        // Apply damping if specified
        if (options.linearDamping !== undefined) {
            aggregate.body.setLinearDamping(options.linearDamping);
        }
        if (options.angularDamping !== undefined) {
            aggregate.body.setAngularDamping(options.angularDamping);
        }

        // Store reference if ID provided
        if (id) {
            this._aggregates.set(id, aggregate);
        }

        return aggregate;
    }

    /**
     * Create a static physics body (mass = 0)
     * Optimized for terrain, walls, and other immovable objects
     */
    public addStaticBody(
        mesh: Mesh | AbstractMesh,
        shapeType: PhysicsShapeType = PhysicsShapeType.MESH,
        id?: string
    ): PhysicsAggregate {
        return this.addPhysicsAggregate(
            mesh,
            shapeType,
            {
                mass: 0,
                friction: 0.8,
                restitution: 0.1,
                disablePreStep: true, // Performance optimization for static bodies
            },
            id
        );
    }

    /**
     * Create a dynamic physics body
     */
    public addDynamicBody(
        mesh: Mesh | AbstractMesh,
        shapeType: PhysicsShapeType,
        mass: number = 1,
        options?: Partial<PhysicsBodyOptions>,
        id?: string
    ): PhysicsAggregate {
        return this.addPhysicsAggregate(
            mesh,
            shapeType,
            {
                mass,
                restitution: options?.restitution ?? 0.3,
                friction: options?.friction ?? 0.5,
                linearDamping: options?.linearDamping ?? 0.1,
                angularDamping: options?.angularDamping ?? 0.1,
                ...options,
            },
            id
        );
    }

    /**
     * Create a kinematic physics body (controlled by code)
     */
    public addKinematicBody(
        mesh: Mesh | AbstractMesh,
        shapeType: PhysicsShapeType,
        id?: string
    ): PhysicsAggregate {
        return this.addPhysicsAggregate(
            mesh,
            shapeType,
            {
                mass: 0,
                startAsKinematic: true,
            },
            id
        );
    }

    /**
     * Add physics to terrain mesh
     * Uses MESH shape type for accurate collision with heightmap
     */
    public addTerrainPhysics(
        terrainMesh: Mesh | AbstractMesh,
        id: string = "terrain"
    ): PhysicsAggregate {
        return this.addStaticBody(terrainMesh, PhysicsShapeType.MESH, id);
    }

    /**
     * Get an aggregate by its ID
     */
    public getAggregate(id: string): PhysicsAggregate | undefined {
        return this._aggregates.get(id);
    }

    /**
     * Remove an aggregate by its ID
     */
    public removeAggregate(id: string): void {
        const aggregate = this._aggregates.get(id);
        if (aggregate) {
            aggregate.dispose();
            this._aggregates.delete(id);
        }
    }

    /**
     * Apply an impulse to a physics body
     */
    public applyImpulse(
        body: PhysicsBody,
        impulse: Vector3,
        contactPoint?: Vector3
    ): void {
        if (contactPoint) {
            body.applyImpulse(impulse, contactPoint);
        } else {
            body.applyImpulse(impulse, body.transformNode.absolutePosition);
        }
    }

    /**
     * Apply a force to a physics body
     */
    public applyForce(
        body: PhysicsBody,
        force: Vector3,
        contactPoint?: Vector3
    ): void {
        if (contactPoint) {
            body.applyForce(force, contactPoint);
        } else {
            body.applyForce(force, body.transformNode.absolutePosition);
        }
    }

    /**
     * Set velocity of a physics body
     */
    public setLinearVelocity(body: PhysicsBody, velocity: Vector3): void {
        body.setLinearVelocity(velocity);
    }

    /**
     * Get velocity of a physics body
     */
    public getLinearVelocity(body: PhysicsBody): Vector3 {
        return body.getLinearVelocity();
    }

    /**
     * Perform a raycast
     */
    public raycast(
        from: Vector3,
        to: Vector3
    ): RaycastResult {
        this._ensureInitialized();

        // Use the physics engine's raycast method
        const physicsEngine = this._scene.getPhysicsEngine();
        if (!physicsEngine) {
            return { hasHit: false };
        }

        const result = physicsEngine.raycast(from, to);
        
        if (result.hasHit) {
            return {
                hasHit: true,
                hitPoint: result.hitPointWorld,
                hitNormal: result.hitNormalWorld,
                distance: result.hitDistance,
                body: result.body,
            };
        }

        return { hasHit: false };
    }

    /**
     * Perform a raycast from a point in a direction
     */
    public raycastFromDirection(
        origin: Vector3,
        direction: Vector3,
        maxDistance: number = 1000
    ): RaycastResult {
        const normalizedDir = direction.normalize();
        const to = origin.add(normalizedDir.scale(maxDistance));
        return this.raycast(origin, to);
    }

    /**
     * Set gravity
     */
    public setGravity(gravity: Vector3): void {
        this._config.gravity = gravity.clone();
        if (this._isInitialized && this._havokPlugin) {
            // Physics engine gravity is set on the scene
            this._scene.getPhysicsEngine()?.setGravity(gravity);
        }
    }

    /**
     * Toggle debug visualization
     */
    public setDebugEnabled(enabled: boolean): void {
        this._debugEnabled = enabled;
        
        if (this._isInitialized) {
            if (enabled) {
                // Create physics viewer if it doesn't exist
                if (!this._physicsViewer) {
                    this._physicsViewer = new PhysicsViewer(this._scene);
                }
                
                // Show all physics bodies
                for (const aggregate of this._aggregates.values()) {
                    if (aggregate.body) {
                        this._physicsViewer.showBody(aggregate.body);
                    }
                }
                
                console.log("PhysicsManager: Debug visualization enabled");
            } else {
                // Hide/dispose physics viewer
                if (this._physicsViewer) {
                    this._physicsViewer.dispose();
                    this._physicsViewer = null;
                }
                
                console.log("PhysicsManager: Debug visualization disabled");
            }
        }
    }

    /**
     * Get debug enabled state
     */
    public get debugEnabled(): boolean {
        return this._debugEnabled;
    }

    // ==================== Time Acceleration/Deceleration Methods ====================

    /**
     * Update physics time scale (call every frame)
     * Handles smooth transitions between time scales
     */
    public update(deltaTime: number): void {
        if (!this._isInitialized) return;
        
        // Update time scale with smooth transition
        this._updateTimeScale(deltaTime);
        
        // Calculate scaled delta time
        this._scaledDeltaTime = this._isPaused ? 0 : deltaTime * this._timeConfig.timeScale;
    }
    
    /**
     * Set the target time scale with smooth transition
     * @param scale Target time scale (0 = paused, 1 = normal, 2 = double, etc.)
     * @param immediate If true, apply immediately without transition
     */
    public setTimeScale(scale: number, immediate: boolean = false): void {
        const clampedScale = Math.max(
            this._timeConfig.minTimeScale,
            Math.min(this._timeConfig.maxTimeScale, scale)
        );
        
        this._timeConfig.targetTimeScale = clampedScale;
        
        if (immediate) {
            this._timeConfig.timeScale = clampedScale;
            this._emitTimeScaleChange();
        }
    }
    
    /**
     * Set time scale using a preset
     * @param preset The time scale preset to use
     * @param immediate If true, apply immediately without transition
     */
    public setTimeScalePreset(preset: TimeScalePreset, immediate: boolean = false): void {
        this.setTimeScale(preset, immediate);
    }
    
    /**
     * Accelerate time (increase time scale)
     * @param amount Amount to increase time scale by
     */
    public accelerateTime(amount: number = 0.5): void {
        this.setTimeScale(this._timeConfig.targetTimeScale + amount);
    }
    
    /**
     * Decelerate time (decrease time scale)
     * @param amount Amount to decrease time scale by
     */
    public decelerateTime(amount: number = 0.5): void {
        this.setTimeScale(this._timeConfig.targetTimeScale - amount);
    }
    
    /**
     * Enter slow motion mode
     * @param scale Slow motion scale (default: 0.25)
     * @param immediate Apply immediately without transition
     */
    public enterSlowMotion(scale: number = TimeScalePreset.SLOW_MOTION, immediate: boolean = false): void {
        this.setTimeScale(scale, immediate);
    }
    
    /**
     * Exit slow motion and return to normal speed
     * @param immediate Apply immediately without transition
     */
    public exitSlowMotion(immediate: boolean = false): void {
        this.setTimeScale(TimeScalePreset.NORMAL, immediate);
    }
    
    /**
     * Toggle slow motion on/off
     * @param slowMoScale Scale to use for slow motion
     */
    public toggleSlowMotion(slowMoScale: number = TimeScalePreset.SLOW_MOTION): void {
        if (this._timeConfig.targetTimeScale < TimeScalePreset.NORMAL) {
            this.exitSlowMotion();
        } else {
            this.enterSlowMotion(slowMoScale);
        }
    }
    
    /**
     * Pause physics time
     */
    public pauseTime(): void {
        this._isPaused = true;
        this._emitTimeScaleChange();
    }
    
    /**
     * Resume physics time
     */
    public resumeTime(): void {
        this._isPaused = false;
        this._emitTimeScaleChange();
    }
    
    /**
     * Toggle pause state
     */
    public togglePause(): void {
        if (this._isPaused) {
            this.resumeTime();
        } else {
            this.pauseTime();
        }
    }
    
    /**
     * Configure time acceleration parameters
     */
    public configureTimeAcceleration(config: Partial<PhysicsTimeConfig>): void {
        this._timeConfig = {
            ...this._timeConfig,
            ...config,
        };
    }
    
    /**
     * Set the acceleration rate for time scale changes
     */
    public setAccelerationRate(rate: number): void {
        this._timeConfig.accelerationRate = Math.max(0.1, rate);
    }
    
    /**
     * Set the deceleration rate for time scale changes
     */
    public setDecelerationRate(rate: number): void {
        this._timeConfig.decelerationRate = Math.max(0.1, rate);
    }
    
    /**
     * Get the current physics time state
     */
    public getTimeState(): PhysicsTimeState {
        return {
            currentTimeScale: this._timeConfig.timeScale,
            targetTimeScale: this._timeConfig.targetTimeScale,
            isAccelerating: this.isAccelerating,
            isDecelerating: this.isDecelerating,
            isPaused: this._isPaused,
            scaledDeltaTime: this._scaledDeltaTime,
        };
    }
    
    /**
     * Register callback for time scale changes
     */
    public onTimeScaleChanged(callback: (state: PhysicsTimeState) => void): void {
        this._onTimeScaleChanged = callback;
    }
    
    /**
     * Reset time scale to normal
     */
    public resetTimeScale(immediate: boolean = true): void {
        this._isPaused = false;
        this.setTimeScale(TimeScalePreset.NORMAL, immediate);
    }
    
    /**
     * Update time scale with smooth interpolation
     */
    private _updateTimeScale(deltaTime: number): void {
        const target = this._timeConfig.targetTimeScale;
        const current = this._timeConfig.timeScale;
        
        if (Math.abs(target - current) < 0.001) {
            if (this._timeConfig.timeScale !== target) {
                this._timeConfig.timeScale = target;
                this._emitTimeScaleChange();
            }
            return;
        }
        
        const rate = current < target 
            ? this._timeConfig.accelerationRate 
            : this._timeConfig.decelerationRate;
        
        const change = rate * deltaTime;
        
        if (current < target) {
            this._timeConfig.timeScale = Math.min(target, current + change);
        } else {
            this._timeConfig.timeScale = Math.max(target, current - change);
        }
        
        this._emitTimeScaleChange();
    }
    
    /**
     * Emit time scale change event
     */
    private _emitTimeScaleChange(): void {
        if (this._onTimeScaleChanged) {
            this._onTimeScaleChanged(this.getTimeState());
        }
    }

    /**
     * Dispose all physics resources
     */
    public dispose(): void {
        // Dispose all tracked aggregates
        for (const aggregate of this._aggregates.values()) {
            aggregate.dispose();
        }
        this._aggregates.clear();

        // Disable physics on scene
        if (this._scene.getPhysicsEngine()) {
            this._scene.disablePhysicsEngine();
        }

        this._havokPlugin = null;
        this._havokInstance = null;
        this._isInitialized = false;

        console.log("PhysicsManager: Disposed");
    }

    /**
     * Ensure physics is initialized before operations
     */
    private _ensureInitialized(): void {
        if (!this._isInitialized) {
            throw new Error("PhysicsManager: Not initialized. Call initialize() first.");
        }
    }
}
